import React, { useState, useEffect } from 'react';
import { roleService } from '../../services/roleService';
import { Role, Permission, PermissionCategory } from '../../types/role';
import { useToast } from '../../contexts/ToastContext';

interface CreateEditRoleModalProps {
  isOpen: boolean;
  editRole: Role | null;
  allCategories: PermissionCategory[];
  onClose: () => void;
  onSuccess: () => void;
}

const CreateEditRoleModal: React.FC<CreateEditRoleModalProps> = ({
  isOpen,
  editRole,
  allCategories,
  onClose,
  onSuccess,
}) => {
  const { toast } = useToast();
  const isEditMode = !!editRole;

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [isSystemRole, setIsSystemRole] = useState(true);
  const [selectedPermissionIds, setSelectedPermissionIds] = useState<Set<string>>(new Set());
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!isOpen) return;
    if (editRole) {
      setName(editRole.name);
      setDescription(editRole.description || '');
      setIsSystemRole(editRole.isSystemRole);
      setSelectedPermissionIds(new Set(editRole.permissions.map(p => p.id)));
    } else {
      setName('');
      setDescription('');
      setIsSystemRole(true);
      setSelectedPermissionIds(new Set());
    }
    setErrors({});
  }, [isOpen, editRole]);

  const togglePermission = (id: string) => {
    setSelectedPermissionIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleCategory = (permissions: Permission[]) => {
    const allSelected = permissions.every(p => selectedPermissionIds.has(p.id));
    setSelectedPermissionIds(prev => {
      const next = new Set(prev);
      permissions.forEach(p => {
        if (allSelected) next.delete(p.id);
        else next.add(p.id);
      });
      return next;
    });
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!name.trim()) newErrors.name = 'Role name is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setIsSubmitting(true);
    try {
      if (isEditMode) {
        const res = await roleService.update(editRole!.id, {
          name: name.trim(),
          description: description.trim() || null,
          permissionIds: Array.from(selectedPermissionIds),
        });
        if (res.success) {
          toast.success(`${name} updated`);
          onClose();
          onSuccess();
        } else {
          toast.error(res.message || 'Failed to update role');
        }
      } else {
        const res = await roleService.create({
          name: name.trim(),
          description: description.trim() || null,
          isSystemRole,
          permissionIds: Array.from(selectedPermissionIds),
        });
        if (res.success) {
          toast.success(`${name} created`);
          onClose();
          onSuccess();
        } else {
          toast.error(res.message || 'Failed to create role');
        }
      }
    } catch (err: any) {
      toast.error(err.response?.data?.message || `Failed to ${isEditMode ? 'update' : 'create'} role`);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm p-4 overflow-y-auto">
      <div
        className="bg-white w-full max-w-2xl rounded-2xl border border-slate-200 shadow-2xl my-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-8 pt-8 pb-6 border-b border-slate-200">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-black tracking-tight text-slate-900">
                {isEditMode ? 'Edit Role' : 'Create New Role'}
              </h2>
              <p className="text-slate-500 text-sm mt-1">
                {isEditMode ? `Editing "${editRole!.name}"` : 'Define a new role with permissions'}
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-slate-500 hover:text-slate-900 transition-colors p-2 hover:bg-slate-50 rounded-lg"
              disabled={isSubmitting}
            >
              <span className="material-symbols-outlined">close</span>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="px-8 py-6 space-y-6 max-h-[60vh] overflow-y-auto">
          {/* Role Info */}
          <div className="space-y-4">
            <h3 className="text-xs font-black uppercase tracking-[0.2em] text-slate-500 flex items-center gap-2">
              <span className="w-1 h-3 bg-primary rounded-full" />
              Role Information
            </h3>

            <div className="space-y-2">
              <label className="text-xs font-bold text-slate-500">Role Name</label>
              <input
                type="text"
                value={name}
                onChange={(e) => { setName(e.target.value); setErrors(er => ({ ...er, name: '' })); }}
                placeholder="e.g. Content Manager"
                className={`w-full bg-slate-50 border ${errors.name ? 'border-red-500/50' : 'border-slate-200'} rounded-lg py-3 px-4 text-sm focus:ring-1 focus:ring-primary focus:border-primary transition-all placeholder:text-slate-400 text-slate-700`}
              />
              {errors.name && <p className="text-xs text-red-500">{errors.name}</p>}
            </div>

            <div className="space-y-2">
              <label className="text-xs font-bold text-slate-500">Description (optional)</label>
              <input
                type="text"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Brief description of this role"
                className="w-full bg-slate-50 border border-slate-200 rounded-lg py-3 px-4 text-sm focus:ring-1 focus:ring-primary focus:border-primary transition-all placeholder:text-slate-400 text-slate-700"
              />
            </div>

            {!isEditMode && (
              <div className="space-y-2">
                <label className="text-xs font-bold text-slate-500">Role Type</label>
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => setIsSystemRole(true)}
                    className={`flex-1 p-3 rounded-xl border text-left transition-all ${
                      isSystemRole
                        ? 'bg-primary/10 border-primary/30 ring-1 ring-primary/20'
                        : 'bg-slate-50 border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <span className={`material-symbols-outlined text-lg ${isSystemRole ? 'text-primary' : 'text-slate-500'}`}>shield</span>
                      <div>
                        <p className={`text-sm font-bold ${isSystemRole ? 'text-primary' : 'text-slate-600'}`}>System Role</p>
                        <p className="text-[10px] text-slate-500">For platform administrators</p>
                      </div>
                    </div>
                  </button>
                  <button
                    type="button"
                    onClick={() => setIsSystemRole(false)}
                    className={`flex-1 p-3 rounded-xl border text-left transition-all ${
                      !isSystemRole
                        ? 'bg-blue-500/10 border-blue-500/30 ring-1 ring-blue-500/20'
                        : 'bg-slate-50 border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <span className={`material-symbols-outlined text-lg ${!isSystemRole ? 'text-blue-400' : 'text-slate-500'}`}>group</span>
                      <div>
                        <p className={`text-sm font-bold ${!isSystemRole ? 'text-blue-400' : 'text-slate-600'}`}>Customer Role</p>
                        <p className="text-[10px] text-slate-500">For brand/tenant users</p>
                      </div>
                    </div>
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Permissions */}
          <div className="space-y-4">
            <h3 className="text-xs font-black uppercase tracking-[0.2em] text-slate-500 flex items-center gap-2">
              <span className="w-1 h-3 bg-primary rounded-full" />
              Permissions ({selectedPermissionIds.size} selected)
            </h3>

            {allCategories.length === 0 ? (
              <p className="text-sm text-slate-500">No permissions available</p>
            ) : (
              <div className="space-y-4">
                {allCategories.map(cat => {
                  const catSelectedCount = cat.permissions.filter(p => selectedPermissionIds.has(p.id)).length;
                  const allCatSelected = catSelectedCount === cat.permissions.length;

                  return (
                    <div key={cat.category} className="bg-slate-50 rounded-xl border border-slate-200 p-4">
                      <button
                        type="button"
                        onClick={() => toggleCategory(cat.permissions)}
                        className="flex items-center gap-2 w-full text-left mb-3"
                      >
                        <div className={`w-4 h-4 rounded border flex items-center justify-center transition-colors ${
                          allCatSelected
                            ? 'bg-primary border-primary text-white'
                            : catSelectedCount > 0
                            ? 'bg-primary/30 border-primary/50 text-white'
                            : 'border-slate-300 hover:border-slate-400'
                        }`}>
                          {(allCatSelected || catSelectedCount > 0) && (
                            <span className="material-symbols-outlined text-xs">
                              {allCatSelected ? 'check' : 'remove'}
                            </span>
                          )}
                        </div>
                        <span className="text-xs font-bold text-slate-600 uppercase tracking-wider">
                          {cat.category}
                        </span>
                        <span className="text-[10px] text-slate-400 ml-auto">
                          {catSelectedCount}/{cat.permissions.length}
                        </span>
                      </button>
                      <div className="flex flex-wrap gap-2">
                        {cat.permissions.map(p => {
                          const isSelected = selectedPermissionIds.has(p.id);
                          return (
                            <button
                              key={p.id}
                              type="button"
                              onClick={() => togglePermission(p.id)}
                              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-all cursor-pointer ${
                                isSelected
                                  ? 'bg-primary/15 text-primary border border-primary/25'
                                  : 'bg-slate-100 text-slate-500 border border-slate-200 hover:border-slate-300 hover:text-slate-500'
                              }`}
                              title={p.description || p.name}
                            >
                              <span className="material-symbols-outlined text-xs">
                                {isSelected ? 'check_box' : 'check_box_outline_blank'}
                              </span>
                              {p.code}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="px-8 py-6 bg-slate-50 border-t border-slate-200 flex items-center justify-end gap-3 rounded-b-2xl">
          <button
            onClick={onClose}
            className="px-6 py-2.5 text-sm font-bold text-slate-500 hover:text-slate-900 transition-colors"
            disabled={isSubmitting}
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={isSubmitting}
            className="bg-primary hover:bg-primary/90 disabled:bg-primary/50 disabled:cursor-not-allowed text-white px-8 py-2.5 rounded-lg font-black text-xs uppercase tracking-widest transition-all transform active:scale-95 shadow-lg shadow-primary/20 flex items-center gap-2"
          >
            {isSubmitting ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                {isEditMode ? 'Updating...' : 'Creating...'}
              </>
            ) : (
              <>
                <span className="material-symbols-outlined text-lg">{isEditMode ? 'save' : 'add'}</span>
                {isEditMode ? 'Update Role' : 'Create Role'}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CreateEditRoleModal;
